<template>
  <div
    :class="{
      'epc-Wrapper-no-table': !showTable
    }"
    :style="{
      '--compensate-chart-outer-offset': `${ADDITIONAL_CHART_CONFIGURATION.layout.padding.right}px`
    }"
    class="epc-Wrapper"
  >
    <div
      class="epc-Header"
      :class="{
        'epc-Header-has-next-sibling': isShowProgressPreview || isShowDataSource
      }"
    >
      <AppTitle :level="4" disable-margin>
        {{ $t(ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.label) }}
      </AppTitle>

      <div class="epc-Header_Actions">
        <portal-target v-if="showTable" :name="portalName" />

        <AppButton
          v-tippy="{
            content: buttonText,
            placement: 'top'
          }"
          :class="{
            'epc-HeaderToggle-collapsed': !isShowChart
          }"
          :data-auto-testid="TOGGLE_PROGRESS_CHART_BUTTON_TEST_ID"
          :data-testid="TOGGLE_PROGRESS_CHART_BUTTON_TEST_ID"
          :size="BUTTON_SIZES.SM"
          class="epc-HeaderToggle"
          icon="toggle-chart"
          type="tertiary-next"
          @click="toggleChart"
        />
        <slot name="actions-append" />
      </div>
    </div>

    <ElementProgressPreview
      v-if="isShowProgressPreview"
      :element
      @open-checkin-modal="onOpenCheckinModal"
    />
    <ElementDataSource
      v-if="isShowDataSource"
      :element="element"
      :type="ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.value"
      @update-element="onUpdateElement"
    />

    <AppDivider v-if="showTable" border-width="2px" no-margin />
    <transition name="slide-table">
      <ElementProgressTable
        v-if="showTable"
        ref="progress-table"
        :element
        :element-id
        :element-target-periods
        :portal-name
        @value-updated="onValueUpdated"
        @threshold-updated="onThresholdUpdated"
        @open-checkin-modal="onOpenCheckinModal"
      />
    </transition>

    <div v-auto-animate="{ duration: ELEMENT_FORM_ANIMATIONS_DURATION }" class="epc-Body">
      <!-- ref="content" -->

      <div
        v-if="isShowChart"
        :data-auto-testid="ELEMENT_PROGRESS_CHART_TEST_ID"
        :data-testid="ELEMENT_PROGRESS_CHART_TEST_ID"
        class="epc-Body_Content"
      >
        <AppDivider no-margin border-width="2px" />

        <div class="epc-Body_Legend">
          <AppTitle :level="4" disable-margin>
            {{ t('common.chart') }}
          </AppTitle>

          <ElementProgressChartLegend :chart-data :progress-color="DEFAULT_CHART_LINE_COLOR" />
        </div>

        <div class="epc-Body_Chart">
          <LineChart
            :additional-chart-configuration="ADDITIONAL_CHART_CONFIGURATION"
            :aspect-ratio="2.2"
            :border-color="DEFAULT_CHART_LINE_COLOR"
            :chart-data
            :chart-type="CHART_TYPES.elementProgress"
            :point-background-color="DEFAULT_CHART_LINE_COLOR"
            :point-hover-background-color="DEFAULT_CHART_LINE_COLOR"
            :target-lines-options
            :tension="0"
            draw-tooltip-vertical-line
            full-size-chart
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { vAutoAnimate } from '@formkit/auto-animate'
import { debounce, isEmpty } from 'lodash'
import { PortalTarget } from 'portal-vue'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import ObjectiveChartsApiHandler from '@/api/objective-charts'
import { useNormalizeChartLines } from '@/composables/objectiveChartComposable'
import { tracker } from '@/tracking/amplitude'
import { ELEMENT_TYPES_FOR_TRACKING } from '@/tracking/amplitude-helpers'
import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import { ELEMENT_PROGRESS_ADJUSTMENT_TABS } from '@/utils/element-types'
import { handleError } from '@/utils/error-handling'
import {
  CHART_TYPES,
  DEFAULT_CHART_LINE_COLOR,
  ELEMENT_PROGRESS_CHART_TYPE_IDS,
  getTargetLinesFillStrategy
} from '@/utils/objective-chart'
import { isKR } from '@/utils/objective-types'
import { DEFAULT_MILESTONES_SETTINGS, isThresholdProgress } from '@/utils/okr-element-values'
import { ELEMENT_FORM_ANIMATIONS_DURATION } from '@/utils/okr-elements-forms-helpers'
import {
  DEFAULT_USERS_SETTINGS,
  IS_SHOW_FORM_CHART,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'

import ElementProgressPreview from '@/components/form/element-progress-preview/ElementProgressPreview'
import ElementProgressTable from '@/components/form/element-progress-table/ElementProgressTable'
import ElementDataSource from '@/components/form/ElementDataSource'
import ElementProgressChartLegend from '@/components/form/ElementProgressChartLegend'
import {
  ELEMENT_PROGRESS_CHART_TEST_ID,
  TOGGLE_PROGRESS_CHART_BUTTON_TEST_ID
} from '@/components/form/jest-helpers'
import LineChart from '@/components/LineChart/LineChart'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppTitle from '@/components/ui/AppTitle/AppTitle'

defineOptions({
  name: 'ElementProgressChart'
})

const props = defineProps({
  elementId: {
    type: [String, Number],
    required: true
  },

  workspaceId: {
    type: [String, Number],
    required: true
  },

  showTable: {
    type: Boolean
  },

  element: {
    type: Object,
    default: () => ({})
  },

  elementTargetPeriods: {
    type: Array,
    default: () => DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
  },

  disableOnEdit: {
    type: Boolean,
    default: false
  }
})

const PLUG_DATA = {
  isPlug: true,
  chartTypeId: 1,
  scales: {
    x: [1741392000000, 1745020800000, 1748649600000, 1752278400000, 1755907200000, 1759536000000],
    y: [0, 500],
    xGranulationId: 3,
    yUnit: ''
  },
  chartLines: [],
  targetLines: []
}

const ADDITIONAL_CHART_CONFIGURATION = {
  layout: {
    padding: {
      right: 9,
      top: 28
    }
  }
}

const { dataSourceLimitations } = usePlansLimitations()

const portalName = computed(() => `epc-chart-header-${props.elementId}`)

const isShowChart = ref(DEFAULT_USERS_SETTINGS[IS_SHOW_FORM_CHART])

const { t } = useI18n()

const buttonText = computed(() =>
  isShowChart.value ? t('objective.chart.collapse') : t('objective.chart.show')
)

// const content = useTemplateRef('content')
// const { height } = useElementSize(content)

const store = useStore()

const isShowChartSavedValue = computed(() => store.getters['system/isShowProgressChart'])

const isSaving = ref(false)

const isMetric = computed(() => isKR(props.element))

const debouncedSaveToUserSettings = debounce(async () => {
  await store.dispatch('system/updateUserSettings', {
    [USER_SETTINGS_MAPPER[IS_SHOW_FORM_CHART]]: isShowChart.value
  })

  const eventName = isShowChart.value ? 'chart shown' : 'chart hidden'

  const element_type = isMetric.value
    ? ELEMENT_TYPES_FOR_TRACKING.METRIC
    : ELEMENT_TYPES_FOR_TRACKING.OBJECTIVE

  tracker.logEvent(eventName, {
    element_type
  })
}, ELEMENT_FORM_ANIMATIONS_DURATION * 2)

const toggleChart = async () => {
  if (isSaving.value) return

  isSaving.value = true
  isShowChart.value = !isShowChart.value
  await debouncedSaveToUserSettings()

  setTimeout(() => {
    isSaving.value = false
  }, ELEMENT_FORM_ANIMATIONS_DURATION)
}

watch(
  isShowChartSavedValue,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      isShowChart.value = newValue
    }
  },
  {
    immediate: true,
    once: true
  }
)

const chartData = ref(PLUG_DATA)

const getChartData = async () => {
  const api = new ObjectiveChartsApiHandler()
  let result = {}
  try {
    result = await api.getChartDataForElement({
      elementId: props.elementId,
      workspaceId: props.workspaceId
    })
    result.chartLines = useNormalizeChartLines(result.chartLines)

    if (result.chartTypeId === ELEMENT_PROGRESS_CHART_TYPE_IDS.VALUES) {
      result.scales.yUnit = ''
    }

    chartData.value = result
  } catch (error) {
    handleError({ error })
  }
}

onMounted(() => {
  getChartData()
})

const progressTableReference = useTemplateRef('progress-table')

const refreshData = () => {
  getChartData()

  if (progressTableReference.value) {
    progressTableReference.value.updateTableData()
  }
}

defineExpose({
  refreshData
})

const emit = defineEmits({
  'value-updated': null,
  'threshold-updated': null,
  'open-checkin-modal': null,
  'update-element': null
})

const onValueUpdated = event => {
  refreshData()
  emit('value-updated', event)
}

const onThresholdUpdated = event => {
  refreshData()
  emit('threshold-updated', event)
}

const onOpenCheckinModal = event => {
  emit('open-checkin-modal', event)
}

const targetLinesOptions = computed(() => {
  return {
    fill: getTargetLinesFillStrategy(props.element)
  }
})

const isShowProgressPreview = computed(() => {
  return isMetric.value && !isThresholdProgress({ element: props.element })
})

const isShowDataSource = computed(() => {
  return (
    isMetric.value &&
    !isEmpty(props.element.dataSourceQuery) &&
    !props.disableOnEdit &&
    dataSourceLimitations.value.isAvailable &&
    !props.element[OKR_ELEMENT_KEYS.EXIST_CONTRIBUTE_CHILDREN]
  )
})

const onUpdateElement = () => {
  emit('update-element')
}
</script>

<style lang="scss" scoped>
.epc-Wrapper {
  border: 2px solid $grey-3-next;
  // padding: 14px 7px 14px 14px;
  border-radius: $border-radius-md-next;
  overflow: hidden;
  --divider-color: #{$grey-3-next};
}

.epc-Header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  overflow: hidden;
  min-height: 62px;
  // padding-right: var(--compensate-chart-outer-offset);
  padding: 18px;

  &-has-next-sibling {
    box-shadow: inset 0 -2px 0 $grey-3-next;
  }
}

.epc-HeaderToggle {
  &:not(&-collapsed) {
    background-color: $grey-3-next;
  }

  &-collapsed {
    background-color: transparent;
  }
}

.epc-Body_Content {
  // padding-top: 28px;
}

.epc-Body_Legend {
  display: flex;
  align-items: center;
  gap: 8px;
  --legend-padding-top: 30px;
  justify-content: space-between;
  padding: var(--legend-padding-top) 18px 6px;

  .epc-Wrapper-no-table & {
    --legend-padding-top: 18px;
  }
}

.epc-Body_Chart {
  // padding-bottom: 20px;
  // margin-bottom: 20px;
  padding: 0 var(--compensate-chart-outer-offset) 14px 18px;
}

.epc-Header_Actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.slide-table-enter-active {
  transition: $menu-transition;
}

.slide-table-leave-active {
  transition: $menu-transition;
}

.slide-table-enter-from,
.slide-table-leave-to {
  opacity: 0;
  transform: translateY(-5%);
}
</style>
